import { Box } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useEffect, useMemo, useState } from 'react';

import { NoConditionsAvailableException } from '@/components/modules/Application/components/PaymentMethod/_shared/exceptions/NoConditionsAvailableException';
import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { NextActions } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/NextActions/NextActions';
import { PaymentIntentList } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/PaymentIntentList/PaymentIntentList';
import { NoConditionsAvailable } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ConditionsAndProgress/Conditions/NoConditionsAvailable';
import { BnplPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/entities/PaymentMethodDetail/BnplPaymentMethodDetailEntity';
import { useBnplPaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/hooks/useBnplPaymentIntent';
import { ConditionsAndProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/ConditionsAndProgress/ConditionsAndProgress';
import { NextActionFactory } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/NextActions/components/NextActionFactory';
import { OrderActivity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/OrderActivity/OrderActivity';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface BnplPaymentMethodProps {
  paymentMethodType: PaymentMethodType;
}

export const BnplPaymentMethod = ({ paymentMethodType }: BnplPaymentMethodProps) => {
  const { paymentMethodDetailEntities } = useCheckoutSessionContext();
  const { refreshPaymentMethodEntity } = useBnplPaymentIntent();
  const { customBorderRadius } = useCustomTheme();

  const [error, setError] = useState<Error | null>(null);

  const paymentMethodDetailEntity = useMemo(
    () => paymentMethodDetailEntities.find((pme) => pme.type === paymentMethodType),
    [paymentMethodDetailEntities, paymentMethodType],
  );

  useEffect(() => {
    const refresh = async () => {
      if (
        paymentMethodDetailEntity &&
        paymentMethodDetailEntity.isSelected === false &&
        paymentMethodDetailEntity.isRefreshed === false &&
        paymentMethodDetailEntities.length > 0
      ) {
        try {
          await refreshPaymentMethodEntity(paymentMethodDetailEntity);
        } catch (error) {
          setError(error as Error);
        }
      }
    };

    refresh();
  }, [paymentMethodDetailEntity]);

  if (error && error instanceof NoConditionsAvailableException) {
    return <NoConditionsAvailable />;
  }

  if (!paymentMethodDetailEntity || !paymentMethodDetailEntity.isRefreshed) {
    return (
      <Skeleton variant="rect" width={'100%'} height={'400px'} style={{ borderRadius: customBorderRadius.paper }} />
    );
  }

  if (!(paymentMethodDetailEntity instanceof BnplPaymentMethodDetailEntity)) {
    return null;
  }

  const showOrderActivity =
    paymentMethodDetailEntity.isPartiallyCaptured() || paymentMethodDetailEntity.isCaptured();

  return (
    <Box display={'flex'} flexDirection={'column'} gridGap={24}>
      <ConditionsAndProgress paymentMethodDetailEntity={paymentMethodDetailEntity} />
      {showOrderActivity ? (
        <OrderActivity paymentMethodDetailEntity={paymentMethodDetailEntity} />
      ) : (
        <NextActions paymentMethodDetailEntity={paymentMethodDetailEntity} NextActionFactory={NextActionFactory} />
      )}
      <PaymentIntentList paymentMethodDetailEntity={paymentMethodDetailEntity} />
    </Box>
  );
};
